import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import type { Experience } from '@/features/experiences/types/experience.types';
import type { Verification } from '@/features/verifications/types/verification.types';
import { getEditDisabledReason, isExperienceLocked } from '../utils/experience-lock';
import { CandidateExperienceVerifications } from './CandidateExperienceVerifications';
import { ContactsUnavailableNotice } from './ContactsUnavailableNotice';
import { ExperienceCardHeader } from './ExperienceCardHeader';
import { ExperienceContactsSummary } from './ExperienceContactsSummary';

interface CandidateExperienceItemProps {
  experience: Experience;
  contacts: Verification[] | null;
  isContactsLoading?: boolean;
  onEdit: (exp: Experience) => void;
  onDelete: (id: string) => void;
}

export function CandidateExperienceItem({
  experience,
  contacts,
  isContactsLoading = false,
  onEdit,
  onDelete,
}: CandidateExperienceItemProps): JSX.Element {
  // A null list means the contact query has not succeeded yet, which must not be
  // rendered as a confirmed empty list: that would invite duplicate contacts.
  const isContactsUnavailable = contacts === null;
  const contactList = contacts ?? [];
  const editDisabledReason = getEditDisabledReason(contacts, isContactsLoading);
  const isLocked = isExperienceLocked(contacts);
  const [isExpanded, setIsExpanded] = useState(contactList.length === 0);

  return (
    <div className="rounded border">
      <ExperienceCardHeader
        experience={experience}
        onEdit={onEdit}
        onDelete={onDelete}
        editDisabledReason={editDisabledReason}
        isLocked={isLocked}
      />
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="border-t">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="h-auto w-full rounded-none px-4 py-3">
              <ExperienceContactsSummary
                statuses={contactList.map((contact) => contact.requestStatus)}
                isExpanded={isExpanded}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {isContactsUnavailable ? (
              <ContactsUnavailableNotice isLoading={isContactsLoading} />
            ) : (
              <CandidateExperienceVerifications
                experience={experience}
                verifications={contactList}
              />
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}
